import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TimestampConverterProps {
  className?: string;
  currentTimestamp?: number;
  onCopyToClipboard?: (text: string) => void;
  copySuccess?: string;
  formatCurrentTime?: () => string;
}

export default function TimestampConverter({
  className = '',
  currentTimestamp,
  onCopyToClipboard,
  copySuccess: externalCopySuccess,
  formatCurrentTime
}: TimestampConverterProps) {
  const { t, i18n } = useTranslation(['timestamp', 'common']);

  // Timestamp 输入
  const [timestampInput, setTimestampInput] = useState('');

  // Date 输入
  const [dateInput, setDateInput] = useState('');

  // 复制状态
  const [copied, setCopied] = useState('');

  // 复制到剪贴板
  const copyToClipboard = (text: string, id: string) => {
    if (onCopyToClipboard) {
      onCopyToClipboard(text);
    } else {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(id);
        setTimeout(() => setCopied(''), 2000);
      });
    }
  };

  // 使用外部或内部的 copied 状态
  const currentCopySuccess = externalCopySuccess || copied;

  // 自动检测时间戳类型（10位=秒，13位=毫秒）
  const detectTimestampType = (timestamp: number): 'seconds' | 'milliseconds' => {
    const timestampStr = timestamp.toString();
    if (timestampStr.length === 10) {
      return 'seconds';
    } else if (timestampStr.length === 13) {
      return 'milliseconds';
    }
    // 默认：如果是11-12位，可能是秒级；如果是14位以上，可能是其他单位
    // 这里用一个简单的启发式规则：小于等于10^11的认为是秒级
    return timestamp <= 10000000000 ? 'seconds' : 'milliseconds';
  };

  // 计算从timestamp转换的date（实时转换，自动识别）
  const dateFromTimestamp = timestampInput ? (() => {
    try {
      const timestamp = parseInt(timestampInput);
      if (isNaN(timestamp)) return { date: '', type: '', detectedType: '' };

      // 自动检测时间戳类型
      const detectedType = detectTimestampType(timestamp);
      const milliseconds = detectedType === 'seconds' ? timestamp * 1000 : timestamp;
      const date = new Date(milliseconds);

      if (isNaN(date.getTime())) return { date: '', type: '', detectedType: '' };

      // 格式化为 ISO 标准格式
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      const formattedDate = i18n.language === 'zh'
        ? `${year}年${month}月${day}日 ${hours}:${minutes}:${seconds}`
        : `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

      const typeLabel = detectedType === 'seconds'
        ? `${t('page.secondsOnly')} (${timestamp.toString().length} ${t('page.digits')})`
        : `${t('page.millisecondsOnly')} (${timestamp.toString().length} ${t('page.digits')})`;

      return { date: formattedDate, type: typeLabel, detectedType };
    } catch {
      return { date: '', type: '', detectedType: '' };
    }
  })() : { date: '', type: '', detectedType: '' };

  // 计算从date转换的timestamp（实时转换）
  const timestampFromDate = dateInput ? (() => {
    try {
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return '';
      return date.getTime().toString();
    } catch {
      return '';
    }
  })() : '';

  const timestampSecondsFromDate = timestampFromDate ?
    Math.floor(parseInt(timestampFromDate) / 1000).toString() : '';

  // 设置当前时间
  const setCurrentTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    setDateInput(`${year}-${month}-${day}T${hours}:${minutes}`);
  };

  // 清空所有输入
  const clearAll = () => {
    setTimestampInput('');
    setDateInput('');
  };

  return (
    <div className={className}>
      {/* 交互式转换器 */}
      <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center">
            <i className="ri-exchange-line mr-3 text-blue-600"></i>
            {t('page.interactiveConverter')}
          </h3>
          {(timestampInput || dateInput) && (
            <button
              onClick={clearAll}
              className="text-gray-600 hover:text-gray-800 text-sm flex items-center transition-colors"
            >
              <i className="ri-close-circle-line mr-1"></i>
              {t('common:button.clear')}
            </button>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Timestamp 输入区域 */}
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 text-gray-800">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-lg">{t('page.timestampToDatetimeTitle')}</h4>
              <div className="text-xs text-gray-500">
                {t('page.autoDetect')}
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-2">
                  {t('page.enterTimestamp')}
                </label>
                <input
                  type="text"
                  value={timestampInput}
                  onChange={(e) => setTimestampInput(e.target.value)}
                  placeholder="1734598784 or 1734598784000"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-lg font-mono transition-colors"
                />
              </div>

              {dateFromTimestamp.date && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center text-green-700 text-xs font-medium mb-1">
                      <i className="ri-arrow-right-line mr-1"></i>
                      {t('page.convertedTo')}
                    </div>
                    <button
                      onClick={() => copyToClipboard(dateFromTimestamp.date, 'date-from-ts')}
                      className="text-green-600 hover:text-green-800 text-xs"
                    >
                      <i className="ri-file-copy-line mr-1"></i>
                      {currentCopySuccess === dateFromTimestamp.date ? t('common:button.copied') : t('common:button.copy')}
                    </button>
                  </div>
                  <div className="text-gray-800 font-medium text-base mb-2">{dateFromTimestamp.date}</div>
                  <div className="text-xs text-gray-500 flex items-center">
                    <i className="ri-information-line mr-1"></i>
                    {t('page.detected')}: {dateFromTimestamp.type}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Date/Time 输入区域 */}
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 text-gray-800">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-lg">{t('page.datetimeToTimestampTitle')}</h4>
              <button
                onClick={setCurrentTime}
                className="text-xs bg-blue-100 text-blue-600 hover:bg-blue-200 px-3 py-1.5 rounded-lg transition-colors flex items-center"
              >
                <i className="ri-time-line mr-1"></i>
                {t('common:button.now')}
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-2">
                  {t('page.enterDateTime')}
                </label>
                <input
                  key={i18n.language}
                  type="datetime-local"
                  value={dateInput}
                  onChange={(e) => setDateInput(e.target.value)}
                  onClick={(e) => {
                    // 触发日期选择器
                    try {
                      (e.target as HTMLInputElement).showPicker?.();
                    } catch (error) {
                      // 某些浏览器可能不支持 showPicker
                      console.log('showPicker not supported');
                    }
                  }}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-lg transition-colors cursor-pointer"
                />
              </div>

              {timestampFromDate && (
                <div className="mt-4 space-y-3">
                  {/* 毫秒级 */}
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center text-purple-700 text-xs font-medium mb-1">
                        <i className="ri-arrow-right-line mr-1"></i>
                        {t('page.milliseconds')}
                      </div>
                      <button
                        onClick={() => copyToClipboard(timestampFromDate, 'ts-ms')}
                        className="text-purple-600 hover:text-purple-800 text-xs"
                      >
                        <i className="ri-file-copy-line mr-1"></i>
                        {currentCopySuccess === timestampFromDate ? t('common:button.copied') : t('common:button.copy')}
                      </button>
                    </div>
                    <div className="text-gray-800 font-mono font-medium">{timestampFromDate}</div>
                  </div>

                  {/* 秒级 */}
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center text-purple-700 text-xs font-medium mb-1">
                        <i className="ri-arrow-right-line mr-1"></i>
                        {t('page.seconds')}
                      </div>
                      <button
                        onClick={() => copyToClipboard(timestampSecondsFromDate, 'ts-sec')}
                        className="text-purple-600 hover:text-purple-800 text-xs"
                      >
                        <i className="ri-file-copy-line mr-1"></i>
                        {currentCopySuccess === timestampSecondsFromDate ? t('common:button.copied') : t('common:button.copy')}
                      </button>
                    </div>
                    <div className="text-gray-800 font-mono font-medium">{timestampSecondsFromDate}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 提示信息 */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <i className="ri-lightbulb-line text-lg mt-0.5 text-blue-600"></i>
            <div className="text-sm text-gray-700">
              <p className="font-medium mb-1">{t('page.converterTip')}</p>
              <p className="text-xs text-gray-600">{t('page.converterTipDesc')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 当前时间戳显示 - 整合成一个整体卡片 */}
      {currentTimestamp !== undefined && (
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">{t('page.currentTimestamp')}</h2>

          {/* 当前日期时间 */}
          {formatCurrentTime && (
            <div className="text-center mb-8">
              <div className="text-sm font-medium text-gray-600 mb-3">{t('page.currentDateTime')}</div>
              <div className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">
                {formatCurrentTime()}
              </div>
            </div>
          )}

          {/* 时间戳信息 - 整合在一起 */}
          <div className="space-y-4">
            {/* 毫秒级时间戳 */}
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center text-purple-700 text-xs font-medium mb-1">
                  <i className="ri-arrow-right-line mr-1"></i>
                  {t('page.milliseconds')}
                </div>
                <button
                  onClick={() => {
                    const text = currentTimestamp.toString();
                    if (onCopyToClipboard) {
                      onCopyToClipboard(text);
                    }
                  }}
                  className="text-purple-600 hover:text-purple-800 text-xs"
                >
                  <i className="ri-file-copy-line mr-1"></i>
                  {externalCopySuccess === currentTimestamp.toString() ? t('common:button.copied') : t('common:button.copy')}
                </button>
              </div>
              <div className="text-2xl md:text-3xl font-mono font-medium text-gray-800 text-center">
                {currentTimestamp}
              </div>
            </div>

            {/* 秒级时间戳 */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center text-blue-700 text-xs font-medium mb-1">
                  <i className="ri-arrow-right-line mr-1"></i>
                  {t('page.seconds')}
                </div>
                <button
                  onClick={() => {
                    const text = Math.floor(currentTimestamp / 1000).toString();
                    if (onCopyToClipboard) {
                      onCopyToClipboard(text);
                    }
                  }}
                  className="text-blue-600 hover:text-blue-800 text-xs"
                >
                  <i className="ri-file-copy-line mr-1"></i>
                  {externalCopySuccess === Math.floor(currentTimestamp / 1000).toString() ? t('common:button.copied') : t('common:button.copy')}
                </button>
              </div>
              <div className="text-2xl md:text-3xl font-mono font-medium text-gray-800 text-center">
                {Math.floor(currentTimestamp / 1000)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
